import { useEffect, useRef, useState } from 'preact/hooks';
import {
  ApiError,
  approveProposal,
  getMailbox,
  getToday,
  hasEverReceivedMail,
  listAllRecordsWithLinks,
  listHouseholdAttention,
  listHouseholdPerformers,
  listProposals,
  rejectProposal,
  transitionCommitmentStatus,
  type HouseholdAttentionItem,
  type HouseholdPerformer,
  type Proposal,
  type TodayCommitmentRow,
} from '../lib/api';
import { capturePastDueLine, commitmentDueLine, decidedOnItsOwnCard, proposalSummaryLine } from '../lib/proposals';
import { bumpRecordsVersion, openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { resolveAttentionDestination, attentionLineFor } from '../lib/attentionRoute';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { activeOnly } from '../lib/retired';
import { AA, LINK, otherEnd } from '../lib/auditionSpread';
import { dayOnTheHorizon } from '../lib/farSide';
import { loadCalendarItems, comingUpDayLabel, type CalendarItem } from '../lib/calendarItems';
import { routePath } from '../routes/route';
import { ClaimYourself } from '../components/ClaimYourself';
import type { RecordRow } from '../lib/api';
import './HouseholdHome.css';
import './farSide.css';

// A3 — TODAY (originally "the household home", A2 spec §4.1/§4.2).
//
// ⚑ 2026-09-19 — THE HOUSEHOLD IS GONE. Huan's ruling: "AN ACCOUNT IS ONE
// CAREER. THERE IS NEVER A SECOND PERFORMER IN IT." This screen used to span
// every child on a shared tenant, with a card per child that drilled into
// one career, and a subject-scope prefix on the URL that narrowed the same
// screen when a parent tapped one. There is now exactly one performer this
// account can ever be about, so the card grid, the performer switcher and
// the scope are all DELETED rather than narrowed to a grid of one — a card
// you tap to arrive where you already are is not a feature.
//
// WHAT REMAINS IS THE TRIAGE ITSELF: what needs this account's attention,
// today, and nothing grouped or scoped, because there is nothing left to
// group or scope between.
//
//   • NEEDS YOU NAMES WHAT IT IS ABOUT, INLINE, when the server says so —
//     that was never a switcher and still is not one; it is the receipt of
//     which record a reminder is about, exactly like every other screen.
//
// ===================================================================
// A 403 ON THE PERFORMER READ IS THIS ACCOUNT'S OWN TODAY, NOT AN ERROR
// ===================================================================
// aa_subject declares min_role_read: 'staff', and the pack seats `child` and
// `helper` at `member`. So when a minor opens this screen the performer read
// is refused — and that refusal IS the design: her Today is this same screen
// with the claim-your-career door absent, never a second "kids" version to
// keep in step.
//
// It must not render as an error. An app that tells a child something went
// wrong, on the screen that is her whole account, has told her she is a
// problem. Every other failure still says exactly what it was
// (describeReadFailure) — this one specific refusal is an answer.
//
// ===================================================================
// OWED, AND NAMED SO IT IS NOT FORGOTTEN
// ===================================================================
// The accent for this pack is deep green #2e6b4e / #7cc2a0 (Huan 2026-08-24; was rose-magenta) with
// "film at carbon's restraint". It is NOT in this stylesheet. A pack accent
// belongs in a `data-pack` block in @vowric/ui (lib/skin.ts's
// SKINNED_VERTICALS, which today lists only rexx and workshop) — a colour
// hand-declared in one screen's CSS is exactly the divergent second set that
// tokens.css's own header warns about, and it would be wrong in light theme
// the day someone opened it there. This screen is built on the semantic
// tokens and puts on the pack's colour the moment the skin exists.

export interface HouseholdHomeProps {
  tenantId: string;
  userName: string;
  onNavigate: (path: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

/** Severity order for the triage list — the server's three values. */
const SEVERITY_RANK: Record<HouseholdAttentionItem['severity'], number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

const SEVERITY_VAR: Record<HouseholdAttentionItem['severity'], string> = {
  critical: 'var(--status-critical)',
  warning: 'var(--status-warning)',
  info: 'var(--status-neutral)',
};

/** How many items show before the expander. Same rule RexxHome states: a
 * count you cannot act on is noise, so the list is capped and the rest are
 * one tap away rather than a number. */
const NEEDS_YOU_CAP = 6;

/** ===================================================================
 *  G-19 — HOW MANY PENDING PROPOSALS TODAY CARRIES BEFORE IT POINTS AT
 *  THE QUEUE.
 *  ===================================================================
 *  A SMALL CONSTANT, NOT A LEVER, and the distinction is the register's
 *  own: a lever is a number an OWNER tunes because their business differs.
 *  Nobody's household differs in how many decisions fit above the fold
 *  before a list stops being a triage and becomes a screen you scroll.
 *
 *  Smaller than NEEDS_YOU_CAP on purpose. A proposal is a decision with two
 *  buttons on it — it is taller and heavier than an attention row, and six
 *  of them would push the household's own reminders off the first screen
 *  entirely. The rest are one tap away in Review rather than a number. */
const PROPOSALS_ON_TODAY = 3;

/** B4 (2026-09-24, Huan's ruling walking Today on a phone): "should Today
 * also show what's coming up this week?" Yes — today through today + 6 days.
 * ⚑ B7 (2026-09-24, `defa9aed`, item 2) REMOVED THE OLDER "this week" strip
 * this constant used to sit beside (holds + sessions only, above the fold) —
 * "Coming up" supersedes it outright, so there is no longer a second,
 * narrower week section to compare it against. Kept as its own constant
 * rather than folded into a shared one regardless, since a future date
 * section is more likely than a second one disappearing. */
const COMING_UP_DAYS = 6;

/** How many rows show before the "more this week" line — same reasoning as
 * NEEDS_YOU_CAP above: a count with nothing to tap is noise. */
const COMING_UP_CAP = 5;

function humanize(key: string): string {
  return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** `2h`, `3d`, `just now` — the same shorthand the other homes use. An
 * unparseable timestamp says nothing rather than "NaN ago". */
function relativeWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function HouseholdHome({ tenantId, userName, onNavigate }: HouseholdHomeProps) {
  const [state, setState] = useState<LoadState>('loading');
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [attention, setAttention] = useState<HouseholdAttentionItem[]>([]);
  const [performers, setPerformers] = useState<HouseholdPerformer[]>([]);
  /** True when the performers read was REFUSED — a child or a helper. Not
   * an error state: it is how the household section stays absent without
   * pretending the household is empty. */
  const [householdWithheld, setHouseholdWithheld] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  /** A5 — the one record in the pack whose TIME OF DAY was decided by
   * somebody else, so the only one that comes to the parent rather than
   * being navigated to. Null until looked for; see HORIZON_DAYS. */
  const [nextDay, setNextDay] = useState<RecordRow | null>(null);
  /** B4 — "Coming up": every dated thing the shared calendar assembler
   * returns for today through today + COMING_UP_DAYS. Null until the first
   * read answers, so a genuinely empty week and "not read yet" never look
   * alike; a failure sets `comingUpFailure` instead and leaves this null —
   * house rule: a failed read is never rendered as an empty week. */
  const [comingUp, setComingUp] = useState<CalendarItem[] | null>(null);
  const [comingUpFailure, setComingUpFailure] = useState<ReadFailure | null>(null);
  /** B7 (2026-09-24, `defa9aed`, item 4) — the household's own forwarding
   * address, to show in place of "Nothing needs you right now." the FIRST
   * time only. Stays null (the safe direction — falls back to today's
   * sentence) until BOTH reads answer AND agree it should show: a real
   * address exists (this deployment has mail-in wired and the household has
   * a usable slug — `getMailbox`'s own null-means-nothing rule) AND the
   * household has never received a notice (`hasEverReceivedMail`). Either
   * read failing, or the account already having a notice, leaves this null
   * — never an invented address, never a guess. On its own effect, same
   * posture as the band/week/coming-up reads above: Today's triage must
   * never wait on it. */
  const [neverReceivedAddress, setNeverReceivedAddress] = useState<string | null>(null);
  const [addressCopied, setAddressCopied] = useState(false);
  /** A7/A8 — is the claim-your-career door open?
   *
   * Not a route: claiming is a thing you do ON Today, and giving it a URL
   * would make an empty account bookmarkable as a form rather than as a
   * home.
   *
   * ⚑ 2026-09-19 — THE SECOND DOOR IS GONE. This used to branch on `who`
   * ('self' vs a parent "setting somebody else up" into the SAME tenant) —
   * exactly the household model the ruling ends. An account is one career,
   * so the only door left is claiming your own: there is nobody else to set
   * up here. A guardian creating a minor's own, separate account is real
   * (see the Hold list) but is not this screen's door — it needs its own
   * account, not a second performer in this one. */
  const [claiming, setClaiming] = useState(false);
  /** ===================================================================
   *  G-19 — THE PENDING PROPOSALS, ON THE SCREEN THEY WERE ALWAYS SAID TO
   *  BE ON.
   *  ===================================================================
   *  Every breakdown forwarded to the household address becomes a proposal,
   *  and the Mail screen has been telling families it is "Waiting on Today"
   *  since that screen shipped. It was not: Today rendered attention items
   *  and nothing else, and the screen that decides a proposal was owned by
   *  another pack, so `#/review` was rewritten to Today by the router's own
   *  vertical gate. The sentence was true about the destination and false
   *  about what was there when you arrived.
   *
   *  ⚑ A FAILED READ IS SAID, NEVER SWALLOWED. The band below fails into
   *  absence because a household with no bookings is not in an error state.
   *  This one cannot: an unread queue and an empty queue look identical, and
   *  the difference is whether somebody's audition is sitting undecided. So
   *  the failure gets a line of its own. */
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [proposalsFailure, setProposalsFailure] = useState<ReadFailure | null>(null);
  /** Which proposal is mid-decision — the id, not a boolean, so two rows
   * cannot disable each other. */
  const [deciding, setDeciding] = useState<string | null>(null);
  /** WHAT HAPPENED, kept on the screen after the row leaves it. A decided
   * proposal that simply vanished would be an acknowledgement; these are the
   * receipts, in the order they were earned. */
  const [decided, setDecided] = useState<{ id: string; line: string }[]>([]);
  /** A refusal, against the row it refused. The server's own words. */
  const [decideError, setDecideError] = useState<Record<string, string>>({});

  /** ===================================================================
   *  B17 §1 — "YOU OWE": AN OPEN COMMITMENT IS A THING TODAY OWES YOU.
   *  ===================================================================
   *  `GET /api/today` already computed `waitingOnMe.commitments` (B7) — this
   *  screen simply never read it. One read, off the critical path, same
   *  posture as the shoot-day band (A5) below: Today's triage must never
   *  wait on it. UNLIKE the band, a failure here is SAID rather than
   *  swallowed — same rule G-19's proposals read follows, for the same
   *  reason: an unread list and an empty one look identical, and the
   *  difference is whether a promise somebody made is sitting undecided. */
  const [commitments, setCommitments] = useState<TodayCommitmentRow[]>([]);
  const [commitmentsFailure, setCommitmentsFailure] = useState<ReadFailure | null>(null);
  const [commitmentDeciding, setCommitmentDeciding] = useState<string | null>(null);
  const [commitmentDone, setCommitmentDone] = useState<{ id: string; line: string }[]>([]);
  const [commitmentError, setCommitmentError] = useState<Record<string, string>>({});

  /** Has this screen ever finished a read? Only the FIRST one is allowed to
   * blank the page. */
  const hasLoaded = useRef(false);
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      // ⚑ ONLY THE FIRST READ BLANKS THE SCREEN.
      //
      // `state === 'loading'` early-returns the whole component, so setting it
      // on EVERY read meant any background refresh unmounted everything on
      // Today — including an open set-up form, which lost its receipt in the
      // same instant the performer was created. The walk hit this three times
      // in three different disguises before the cause was this line.
      //
      // A re-read is also just wrong to render as "One moment…": the screen
      // already has an answer, and replacing it with a spinner is a flash
      // that tells the reader less than the stale content did.
      if (!hasLoaded.current) setState('loading');
      setFailure(null);
      try {
        // The two reads are independent and the household one may legitimately
        // refuse, so they are settled separately rather than raced through
        // Promise.all — where one refusal would take the other's answer down
        // with it and blank a child's whole screen.
        const items = await listHouseholdAttention(tenantId);
        if (cancelled) return;
        setAttention(
          [...items].sort(
            (a, b) =>
              SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
              // Newest first inside a severity. ISO strings compare
              // lexicographically; three-way so equal keys return 0.
              (a.lastRaisedAt < b.lastRaisedAt ? 1 : a.lastRaisedAt > b.lastRaisedAt ? -1 : 0)
          )
        );

        try {
          const rows = await listHouseholdPerformers(tenantId);
          if (cancelled) return;
          setPerformers(rows);
          setHouseholdWithheld(false);
        } catch (err) {
          if (cancelled) return;
          if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
            // 403: a child or a helper — the section is not hers.
            // 404: this tenant's pack has no performers at all.
            // Both are answers. See this file's header.
            setPerformers([]);
            setHouseholdWithheld(true);
          } else {
            throw err;
          }
        }
        hasLoaded.current = true;
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setState('error');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, retryNonce, recordsVersion]);

  // ===================================================================
  // G-19 — THE REVIEW QUEUE, READ ON ITS OWN EFFECT.
  // ===================================================================
  // ⚑ AND NOT READ AT ALL FOR A CHILD OR A HELPER, WHICH IS THE WHOLE OF
  // THE SCOPE RULE HERE.
  //
  // `GET /api/proposals` is a tenant-wide read at the `viewer` floor — it
  // carries no aboutness walk, because a proposal is not a record and the
  // pack's `readScope` has nothing to narrow. So rendering it unconditionally
  // would hand a nine-year-old every decision in the household, which is
  // precisely the widening the pack's read scope exists to prevent
  // (aa_audition/aa_session are CHILD_ONLY; aa_subject is above her seat
  // entirely). `householdWithheld` is already this screen's own answer to
  // "is this a child or a helper" — the 403 on the performers read — so it
  // is the gate, rather than a second question asked a second way.
  //
  // A child's Today therefore shows HER OWN pending proposals and no others.
  // Today that set is empty: a mail-raised proposal records no subject at
  // all, and the two subjects a capture can record on her screens are her
  // audition and her tape, not her. The row is the door that opens when one
  // does resolve to her; nothing here has to change for it to appear.
  useEffect(() => {
    if (state !== 'ready' || householdWithheld) {
      setProposals([]);
      setProposalsFailure(null);
      return;
    }
    let cancelled = false;
    async function look() {
      try {
        const rows = await listProposals(tenantId, 'pending');
        if (cancelled) return;
        setProposals(rows);
        setProposalsFailure(null);
      } catch (err) {
        if (cancelled) return;
        setProposals([]);
        setProposalsFailure(describeReadFailure(err));
      }
    }
    void look();
    return () => {
      cancelled = true;
    };
  }, [tenantId, state, householdWithheld, retryNonce, recordsVersion]);

  /** Approve, or decline, IN PLACE — through the same two doors the Review
   * screen uses. A second write path is how two surfaces come to disagree
   * about what "approved" did, and the capture apply logic lives behind
   * exactly one of them. */
  async function decide(p: Proposal, act: 'approve' | 'decline') {
    // ⚑ B16 §5 — ONE FULL STOP. `proposalSummaryLine` already ends most
    // summaries with one (a capture's "Note: “…”" excepted) — appending a
    // second sentence straight after it read as "…Dana Okafor**..** Nothing
    // was filed from it." A summary's own trailing stop is stripped before
    // either receipt adds its own punctuation, approve included (nothing
    // trails it today, but the next line that does inherits the same rule
    // rather than a second, drifting copy of it).
    const what = proposalSummaryLine(p).replace(/\.+\s*$/, '');
    setDeciding(p.id);
    setDecideError((cur) => ({ ...cur, [p.id]: '' }));
    try {
      if (act === 'approve') {
        await approveProposal(tenantId, p.id);
      } else {
        await rejectProposal(tenantId, p.id, 'declined from Today');
      }
      setProposals((cur) => cur.filter((x) => x.id !== p.id));
      setDecided((cur) => [
        {
          id: p.id,
          line: act === 'approve' ? `Approved — ${what}` : `Declined — ${what}. Nothing was filed from it.`,
        },
        ...cur,
      ]);
      // What was approved may have become a record the rest of the screen
      // counts. Re-read behind the receipt, never instead of it.
      if (act === 'approve') bumpRecordsVersion();
    } catch (err) {
      setDecideError((cur) => ({
        ...cur,
        [p.id]:
          err instanceof ApiError
            ? err.message
            : 'The decision was not recorded. It is still waiting.',
      }));
    } finally {
      setDeciding(null);
    }
  }

  /** B7 (2026-09-24, `defa9aed`, item 4) — same shape as AuditionDetail.tsx's
   * `copyLink`: writes, then flips the button's own label rather than a
   * toast the reader might not be looking at. A failed write (an insecure
   * context, a denied permission) leaves the label alone — the address is
   * still on the screen to select by hand, so nothing is lost, only the
   * shortcut. */
  async function copyAddress(address: string) {
    try {
      await navigator.clipboard.writeText(address);
      setAddressCopied(true);
    } catch {
      setAddressCopied(false);
    }
  }

  // ===================================================================
  // B17 §1 — "YOU OWE", ON ITS OWN EFFECT AND OFF THE CRITICAL PATH.
  // ===================================================================
  // Same shape as G-19's proposals read just above: independent of the
  // `load()` effect (a household with no open commitments is not in an
  // error state), but a genuine failure is said rather than swallowed —
  // an unread list and an empty one look identical otherwise.
  useEffect(() => {
    let cancelled = false;
    async function look() {
      try {
        const today = await getToday(tenantId);
        if (cancelled) return;
        setCommitments(today.waitingOnMe.commitments);
        setCommitmentsFailure(null);
      } catch (err) {
        if (cancelled) return;
        setCommitments([]);
        setCommitmentsFailure(describeReadFailure(err));
      }
    }
    void look();
    return () => {
      cancelled = true;
    };
  }, [tenantId, retryNonce, recordsVersion]);

  /** "Done", in place — the one act §1 offers. `transitionCommitmentStatus`'s
   * own table (core/commitments/commitments.ts) makes 'done' TERMINAL: there
   * is no transition back to 'open', so this receipt carries no Undo — see
   * this file's own report on why, and the B16-proposals `decide()` above
   * for the receipt shape this mirrors. */
  async function doneCommitment(c: TodayCommitmentRow) {
    setCommitmentDeciding(c.id);
    setCommitmentError((cur) => ({ ...cur, [c.id]: '' }));
    try {
      await transitionCommitmentStatus(tenantId, c.id, 'done');
      setCommitments((cur) => cur.filter((x) => x.id !== c.id));
      setCommitmentDone((cur) => [{ id: c.id, line: `Done — ${c.title.replace(/\.+\s*$/, '')}.` }, ...cur]);
    } catch (err) {
      setCommitmentError((cur) => ({
        ...cur,
        [c.id]: err instanceof ApiError ? err.message : 'The change was not recorded. It is still waiting.',
      }));
    } finally {
      setCommitmentDeciding(null);
    }
  }

  // ===================================================================
  // A5 — THE SHOOT DAY BAND, ON ITS OWN EFFECT AND OFF THE CRITICAL PATH.
  // ===================================================================
  // This deliberately does NOT live in the load above, and the first draft
  // that put it there is why the rule is written down: awaiting it before
  // `setState('ready')` made Today's whole triage list wait on a read for
  // records most households do not have yet. A parent with no bookings would
  // have stared at a loading screen while a request for shoot days timed
  // out.
  //
  // THE BAND IS AN ADDITION TO TODAY, NEVER A PRECONDITION FOR IT. It
  // appears when it resolves and the screen is complete without it — which
  // is also why every failure here is swallowed into "no band" rather than
  // surfaced: a household that owns no engagements is not in an error state,
  // and a child whose read is refused should simply not see one.
  useEffect(() => {
    let cancelled = false;
    async function look() {
      try {
        const days = await listAllRecordsWithLinks(tenantId, AA.SHOOT_DAY, {
          linkTypes: [LINK.SHOOT_DAY_ENGAGEMENT],
        });
        if (cancelled) return;
        setNextDay(dayOnTheHorizon(activeOnly(days.records), HORIZON_DAYS));
      } catch (err) {
        // The SCREEN stays silent — the band is an addition, and a child
        // whose read is refused should simply not see one. The LOG does not:
        // a 500 swallowed into "no band" is indistinguishable from a
        // household that owns no shoot days, and that silence is exactly what
        // made the Day 3 walk spend an afternoon deciding whether this band
        // was broken (it was not — the day was six days out, past
        // HORIZON_DAYS). Loud where an operator can see it, quiet where a
        // family can.
        console.warn('[today] the shoot-day read failed — no band this render:', err);
        if (!cancelled) setNextDay(null);
      }
    }
    void look();
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordsVersion]);

  // ===================================================================
  // B4 — "COMING UP", ON ITS OWN EFFECT, AND A FAILURE HERE IS SAID.
  // ===================================================================
  // Reads the SAME assembler the calendar itself calls (`loadCalendarItems`)
  // so this list can never disagree with what `#/calendar` shows for the
  // same week — plan gating included, since a Free tenant's `agenda.holds`
  // is already empty at the source (the pack writes none below Working).
  //
  // ⚑ UNLIKE THE BAND ABOVE, A FAILURE HERE IS NOT SWALLOWED. House rule: a
  // silent zero reads exactly like an empty database. `comingUp` stays null
  // on failure (never `[]`) so the empty sentence and "the read failed" can
  // never render as each other. Its own effect, independent of "Needs you":
  // neither may block the other.
  useEffect(() => {
    let cancelled = false;
    async function look() {
      try {
        const now = new Date();
        const from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const to = new Date(from.getTime() + COMING_UP_DAYS * 86_400_000);
        const items = await loadCalendarItems(tenantId, from, to);
        if (cancelled) return;
        setComingUp(items);
        setComingUpFailure(null);
      } catch (err) {
        if (cancelled) return;
        setComingUp(null);
        setComingUpFailure(describeReadFailure(err));
      }
    }
    void look();
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordsVersion, retryNonce]);

  // ===================================================================
  // B7 (2026-09-24, `defa9aed`, item 4) — THE FIRST-EVER "NOTHING NEEDS
  // YOU", REPLACED WITH THE HOUSEHOLD'S OWN ADDRESS.
  // ===================================================================
  // Both reads run together — neither answer means anything without the
  // other, so there is nothing to gain by sequencing them — and BOTH must
  // say yes before the address renders. `getMailbox` already returns null
  // for "no mail-in domain on this deployment" and "no usable slug" (its own
  // header); this effect adds nothing on top of that rule, it only ALSO
  // requires "never received one".
  useEffect(() => {
    let cancelled = false;
    async function look() {
      try {
        const [address, everReceived] = await Promise.all([
          getMailbox(tenantId),
          hasEverReceivedMail(tenantId),
        ]);
        if (cancelled) return;
        setNeverReceivedAddress(address && !everReceived ? address : null);
      } catch {
        // Never an invented address. A failed read of either half falls back
        // to today's ordinary sentence — the same rule every other optional
        // section on this screen already follows.
        if (!cancelled) setNeverReceivedAddress(null);
      }
    }
    void look();
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordsVersion, retryNonce]);

  /** The doorway for one item. Null when the subject cannot be resolved at
   * all — the tile then renders inert rather than as a dead handle. */
  function attentionAction(item: HouseholdAttentionItem): (() => void) | null {
    const dest = resolveAttentionDestination(item, 'actor_assist');
    if (!dest) return null;
    if (dest.kind === 'editor') {
      return () => openRecordEditor({ mode: 'edit', typeKey: dest.typeKey, id: dest.id });
    }
    return () => onNavigate(dest.path);
  }

  if (state === 'loading') {
    return (
      <div class="hh">
        <div class="hh__eyebrow">Today</div>
        <div class="hh__say">One moment…</div>
      </div>
    );
  }

  if (state === 'error' && failure !== null) {
    // A refused read is never rendered as a quiet day. It says which
    // refusal it was, and offers the retry that actually works.
    return (
      <div class="hh">
        <div class="hh__eyebrow">Today</div>
        <div class="hh__say">
          {failure.headline}
          <br />
          <span class="hh__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="hh__more" onClick={() => setRetryNonce((n) => n + 1)}>
            Read today again
          </button>
        ) : null}
      </div>
    );
  }

  // ⚑ 2026-09-19 — NO MORE NARROWING. Both lists used to be filtered by
  // whichever performer the app was scoped into; an account is one career
  // now, so every attention item and every pending proposal this tenant
  // holds is already this account's own. Nothing here is filtered.
  const shown = expanded ? attention : attention.slice(0, NEEDS_YOU_CAP);
  const hidden = attention.length - Math.min(NEEDS_YOU_CAP, attention.length);

  const proposalsShown = proposals.slice(0, PROPOSALS_ON_TODAY);
  const proposalsElsewhere = proposals.length - proposalsShown.length;

  /** An account that has never claimed its own performer record. Not the
   * same as a quiet day, and the screenshot of the first A8 walk is why this
   * distinction is in the code: day one opened with "Nothing needs you right
   * now" ABOVE the only thing on the screen that did. An empty triage list
   * IS a good day for an established account; on one with nobody claimed
   * yet, it is a sentence that is technically true and completely wrong. */
  const unstarted = !householdWithheld && performers.length === 0 && state === 'ready';
  /** …and with nothing to triage, the heading over it is furniture around
   * nothing. Suppressed only when the list is genuinely empty. */
  const showNeedsYou = !(
    unstarted &&
    attention.length === 0 &&
    proposals.length === 0 &&
    decided.length === 0 &&
    proposalsFailure === null
  );

  /** B4 — THE HEADING NAMES THE PERFORMER, NOT THE SIGNED-IN USER. Same read
   * (`listHouseholdPerformers`) and the same field the calendar's own
   * `performerName` map uses (`p.name` — the professional name the pack
   * already resolves server-side). An account is one career, so there is at
   * most one row here.
   *
   * Falls back to `userName` whenever this render has no performer name to
   * show — an account that has not finished setup (`performers.length ===
   * 0`) is the case the brief names, and a child/helper's refused read
   * (`householdWithheld`) folds into the same fallback rather than a second
   * rule: her Today cannot know the performer's name from this render
   * either way (see this file's header on why that read is refused at all). */
  const performerHeading = performers[0]?.name.trim();
  const headingName = performerHeading ? performerHeading : userName;

  return (
    <div class="hh">
      <div class="hh__eyebrow">Today</div>
      <div class="hh__say">{headingName}</div>

      {/* ===== THE SHOOT DAY BAND =====
          ABOVE "Needs you", and that ordering is the design. Triage is a
          list of things the parent should get to; a call time is a thing
          that happens at 5:40am whether they got to it or not. A decided
          time outranks a suggested one.

          It is also the ONLY place the far side of the seam reaches into
          this screen, deliberately: everything else over there is navigated
          to, and this is the difference between a call sheet you open in the
          car and one you never open. */}
      {nextDay ? (
        <button
          type="button"
          class="hh__band"
          onClick={() => onNavigate(routePath({ screen: 'shootDay', id: nextDay.id }))}
        >
          <span class="hh__band-k">
            {bandWhen(nextDay.fields.shoot_date)}
            {typeof nextDay.fields.day_label === 'string' && nextDay.fields.day_label.trim()
              ? ` · ${nextDay.fields.day_label.trim()}`
              : ''}
          </span>
          <span class={`hh__band-v${bandPickup(nextDay.fields.pickup_at) ? '' : ' hh__band-v--absent'}`}>
            {bandPickup(nextDay.fields.pickup_at) ?? 'no call time yet'}
          </span>
          {typeof nextDay.fields.location === 'string' && nextDay.fields.location.trim() ? (
            <span class="hh__band-sub">{nextDay.fields.location.trim()}</span>
          ) : null}
        </button>
      ) : null}

      {/* ===================================================================
          A8 — CLAIMING YOUR OWN CAREER. NOT A FIELD, AND NOT A CHOICE.

          ⚑ 2026-09-19 — THE QUESTION IS GONE, NOT JUST ANSWERED. A7/A8 asked
          "who is this account for?" because a tenant used to be able to hold
          several performers. An account is one career now, so the answer is
          always "you" — asking still would be a question with one honest
          reply, which is worse than not asking. The empty state goes
          straight to the one thing there is to do: say how your own name
          appears.

          ⚑ ABOVE "NEEDS YOU", AND ONLY WHILE THE ACCOUNT IS UNSTARTED. The
          first screenshot of the A8 walk opened with "Nothing needs you right
          now" sitting above the only thing on the screen that did. See
          `unstarted` above for why an empty triage list is a good day for an
          established account and a wrong sentence for one with nobody
          claimed yet. */}
      {!householdWithheld && (claiming || unstarted) ? (
        <div class="hh__begin">
          <h2 class="hh__begin-title">How your name appears</h2>
          <ClaimYourself
            tenantId={tenantId}
            onAdded={() => setRetryNonce((n) => n + 1)}
            onClose={() => setClaiming(false)}
          />
        </div>
      ) : null}

      {showNeedsYou ? (
        <div class="hh__sec">
          <span>Needs you</span>
          {attention.length > NEEDS_YOU_CAP && !expanded ? (
            <span class="hh__mini">
              {NEEDS_YOU_CAP} of {attention.length}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* ===================================================================
          G-19 — THE PENDING PROPOSALS, ABOVE THE REMINDERS.

          A proposal is a DECISION somebody is waiting on; an attention item
          is a reminder. The same argument the shoot-day band makes over this
          whole section applies one level down: a thing that is waiting on
          your say-so outranks a thing that is waiting on your attention.

          Each row is a door into the full card, and the two acts sit beside
          it rather than inside it — a button inside a button is invalid, and
          more to the point the row and the acts answer different questions
          ("what is this?" and "yes or no?").
          =================================================================== */}
      {!showNeedsYou ? null : (
        <>
          {proposalsFailure ? (
            // AN UNREAD QUEUE AND AN EMPTY ONE LOOK IDENTICAL. Saying which
            // is the whole point of this line.
            <div class="hh__empty" data-testid="today-proposals-failure">
              {proposalsFailure.headline}
              <br />
              <span class="hh__say-soft">{proposalsFailure.detail}</span>
            </div>
          ) : null}

          {proposalsShown.map((p) => {
            const onItsCard = decidedOnItsOwnCard(p);
            const pastDue = capturePastDueLine(p);
            return (
              <div class="hh__prop" key={p.id} data-testid="today-proposal">
                <button
                  type="button"
                  class="hh__attn hh__prop-row"
                  onClick={() => onNavigate(routePath({ screen: 'review' }))}
                >
                  <span class="hh__stripe" style={{ background: SEVERITY_VAR.warning }} />
                  <span class="hh__attn-tx">
                    {/* B3: the row now clamps to two lines instead of one —
                        the title attribute keeps the full text reachable
                        (a long-press or hover) when it still runs past that. */}
                    <b class="hh__prop-title" title={proposalSummaryLine(p)}>
                      {proposalSummaryLine(p)}
                    </b>
                    {/* ⚑ APPROVE MUST NEVER COMMIT CONTENT THE SCREEN DID NOT
                        SHOW — Review's own rule, which this row broke
                        (2026-09-16, the Day 3–4 walk, item 2).
                        The rationale is the line the capture lane writes when
                        it must say something it could not do: how many lines
                        nothing could read, and — the walk's case — "Nobody was
                        attached — say whose this is after approving." Review
                        renders it beneath the card. This row renders only the
                        summary and puts an APPROVE BUTTON beside it, so a
                        parent who acts from Today decides without the sentence
                        ever having been on a screen. It is the same silence the
                        server had already ended. */}
                    {/* B21 §3 — a submission-reply proposal's rationale is
                        sometimes the EXACT summary sentence again ("Jarvis
                        Vowric replied about … — mark it invited?" as both
                        title and this line). Rendering it a second time said
                        nothing new; it just repeated itself. Compared
                        trimmed, same as the summary's own trailing-stop
                        stripping just below (`decide()`'s own comment on
                        why one full stop, not two). */}
                    {p.rationale && p.rationale.trim() !== proposalSummaryLine(p).trim() ? (
                      <small class="hh__prop-why">{p.rationale}</small>
                    ) : null}
                    <small>
                      {relativeWhen(p.createdAt)}
                      {/* B3: a proposal that will create something already
                          overdue looked exactly like a current one — meaning
                          in words (canon §2.7), beside the age, never colour
                          alone. Approving still works; nothing is hidden. */}
                      {pastDue ? <span class="hh__prop-pastdue"> · {pastDue}</span> : null}
                    </small>
                  </span>
                </button>
                <span class="hh__prop-acts">
                  {onItsCard ? (
                    // ⚑ APPROVE MUST NEVER COMMIT CONTENT THE SCREEN DID NOT
                    // SHOW — Review's own rule. These kinds are decided by
                    // editing what will be sent, so the row offers the way in
                    // rather than a button that would send the draft unread.
                    <button
                      type="button"
                      class="hh__prop-go"
                      onClick={() => onNavigate(routePath({ screen: 'review' }))}
                    >
                      Read it first
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        class="hh__prop-go"
                        disabled={deciding === p.id}
                        onClick={() => void decide(p, 'approve')}
                      >
                        {deciding === p.id ? 'One moment…' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        class="hh__prop-no"
                        disabled={deciding === p.id}
                        onClick={() => void decide(p, 'decline')}
                      >
                        Decline
                      </button>
                    </>
                  )}
                </span>
                {decideError[p.id] ? (
                  <span class="hh__prop-note" data-testid="today-proposal-error">
                    {decideError[p.id]}
                  </span>
                ) : null}
              </div>
            );
          })}

          {proposalsElsewhere > 0 ? (
            // The rest are a tap away rather than a number — the same rule
            // the attention expander states, except these live on their own
            // screen, so this is a door and not a toggle.
            <button
              type="button"
              class="hh__more"
              data-testid="today-proposals-all"
              onClick={() => onNavigate(routePath({ screen: 'review' }))}
            >
              {proposalsElsewhere} more waiting on you →
            </button>
          ) : null}

          {/* ===================================================================
              B17 §1 — "YOU OWE", AFTER THE PROPOSALS. An approved commitment
              is a promise already made; a proposal is still a decision. The
              same "waiting on your say-so outranks a reminder" ordering the
              proposals block states over attention items applies one level
              down again: a decision still open outranks a promise already
              kept only by remembering it.
              =================================================================== */}
          {commitmentsFailure ? (
            <div class="hh__empty" data-testid="today-commitments-failure">
              {commitmentsFailure.headline}
              <br />
              <span class="hh__say-soft">{commitmentsFailure.detail}</span>
            </div>
          ) : null}

          {commitments.length > 0 ? (
            <>
              <div class="hh__sec">
                <span>You owe</span>
              </div>
              {commitments.map((c) => (
                <div class="hh__prop" key={c.id} data-testid="today-commitment">
                  <span class="hh__attn hh__prop-row">
                    <span class="hh__stripe" style={{ background: SEVERITY_VAR.warning }} />
                    <span class="hh__attn-tx">
                      <b>{c.title}</b>
                      <small>
                        {commitmentDueLine(c.dueAt)}
                        {c.overdue ? ' — passed' : ''}
                        {c.counterpartyName ? ` · to ${c.counterpartyName}` : ''}
                      </small>
                    </span>
                  </span>
                  <span class="hh__prop-acts">
                    <button
                      type="button"
                      class="hh__prop-go"
                      disabled={commitmentDeciding === c.id}
                      onClick={() => void doneCommitment(c)}
                    >
                      {commitmentDeciding === c.id ? 'One moment…' : 'Done'}
                    </button>
                  </span>
                  {commitmentError[c.id] ? (
                    <span class="hh__prop-note" data-testid="today-commitment-error">
                      {commitmentError[c.id]}
                    </span>
                  ) : null}
                </div>
              ))}
            </>
          ) : null}

          {/* B17 §1 — the receipt. NO Undo: `transitionCommitmentStatus`'s
              own table makes 'done' terminal (never back to 'open'), so
              unlike the proposal receipt above this one offers no way back —
              see this file's own report on the contradiction with the
              brief's proposed copy. */}
          {commitmentDone.map((r) => (
            <div class="hh__prop-receipt" key={r.id} data-testid="today-commitment-receipt">
              {r.line}
            </div>
          ))}

          {/* WHAT YOU JUST DECIDED, still on the screen. A row that vanished
              would be an acknowledgement; this is the receipt. */}
          {decided.map((r) => (
            <div class="hh__prop-receipt" key={r.id} data-testid="today-proposal-receipt">
              {r.line}
            </div>
          ))}

          {shown.length === 0 &&
          proposals.length === 0 &&
          decided.length === 0 &&
          commitments.length === 0 &&
          commitmentDone.length === 0 ? (
            neverReceivedAddress ? (
              // B7 (2026-09-24, `defa9aed`, item 4) — THE FIRST TIME, THE
              // ADDRESS ITSELF. A fact, not an instruction: it names what is
              // already true (this is the household's own address) rather
              // than telling the reader what to do with it. Disappears for
              // good the moment one notice arrives — `hasReceivedInboundMail`
              // above.
              <div class="hh__empty hh__addr" data-testid="today-address-empty">
                <div class="hh__addr-line">
                  Your address: <span class="hh__addr-v">{neverReceivedAddress}</span>
                </div>
                <button
                  type="button"
                  class="hh__addr-copy"
                  onClick={() => void copyAddress(neverReceivedAddress)}
                >
                  {addressCopied ? 'Copied' : 'Copy'}
                </button>
                <div class="hh__addr-sub">
                  Forward an audition notice here — it's read for you, and you approve what it finds.
                </div>
              </div>
            ) : (
              // AN EMPTY TRIAGE LIST IS A GOOD DAY. Not an error, not an
              // onboarding prompt, not a call to add something — the spec is
              // explicit that this line must not read like a failure.
              <div class="hh__empty" data-testid="today-needsyou-empty">Nothing needs you right now.</div>
            )
          ) : (
            shown.map((item) => {
              const act = attentionAction(item);
              const body = (
                <>
                  <span class="hh__stripe" style={{ background: SEVERITY_VAR[item.severity] }} />
                  <span class="hh__attn-tx">
                    <b>
                      {/* WHOSE IT IS, INLINE. Part of the item, never a column
                          and never a heading — see this file's header. Absent
                          when the item is not about one performer (a
                          household-level expense), and then the line simply
                          does not claim an owner. */}
                      {item.subject ? <em class="hh__who">{item.subject.name}</em> : null}
                      {/* ⚑ The file-scanning trio's `reason` is audit-log voice
                          and must not lead a parent's tile — the rule is shared
                          with Today via lib/attentionRoute.ts rather than copied
                          onto each screen. Tranche 6 first put it on Today.tsx
                          alone, which this pack never renders. */}
                      {attentionLineFor(item) ?? humanize(item.itemType)}
                    </b>
                    <small>{relativeWhen(item.lastRaisedAt)}</small>
                  </span>
                </>
              );
              return act ? (
                <button type="button" class="hh__attn" key={item.id} onClick={act}>
                  {body}
                </button>
              ) : (
                <div class="hh__attn hh__attn--inert" key={item.id}>
                  {body}
                </div>
              );
            })
          )}
        </>
      )}

      {hidden > 0 ? (
        <button
          type="button"
          class="hh__more"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? '– show fewer ▴' : `+ ${hidden} more ▾`}
        </button>
      ) : null}

      {/* ===================================================================
          B4 — "COMING UP", BELOW NEEDS YOU.
          ===================================================================
          Huan's ruling, 2026-09-24: Today should also say what's coming up
          this week. Reads the SAME items the calendar shows (see the effect
          above) — never a second, drifting assembler — capped at
          COMING_UP_CAP, soonest first (loadCalendarItems already sorts).
          Rendered only once the read has answered one way or the other, so
          the heading never sits over a blank beat before the first response. */}
      {comingUpFailure || comingUp !== null ? (
        <>
          <div class="hh__sec hh__sec--later">
            <span>Coming up</span>
          </div>
          {comingUpFailure ? (
            // A refused or failed read is never the quiet "nothing this
            // week" line — the house rule this whole section exists to
            // prove: a silent zero reads exactly like an empty database.
            <div class="hh__empty" data-testid="today-comingup-failure">
              {comingUpFailure.headline}
              <br />
              <span class="hh__say-soft">{comingUpFailure.detail}</span>
            </div>
          ) : comingUp!.length === 0 ? (
            <div class="hh__empty" data-testid="today-comingup-empty">
              Nothing on the calendar this week.
            </div>
          ) : (
            <>
              {comingUp!.slice(0, COMING_UP_CAP).map((it) => (
                <div class="hh__comingup" key={it.id} data-testid="today-comingup-item">
                  <span class="hh__comingup-when">
                    {comingUpDayLabel(it.day)} · {it.when}
                  </span>
                  <span class="hh__comingup-what">{it.what}</span>
                </div>
              ))}
              {comingUp!.length > COMING_UP_CAP ? (
                <button
                  type="button"
                  class="hh__more"
                  data-testid="today-comingup-all"
                  onClick={() => onNavigate(routePath({ screen: 'calendar' }))}
                >
                  {comingUp!.length - COMING_UP_CAP} more this week →
                </button>
              ) : null}
            </>
          )}
        </>
      ) : null}

      {/* ⚑ 2026-09-19 — THE HOUSEHOLD GRID IS DELETED, NOT NARROWED TO ONE
          CARD. It used to show every performer on the tenant, each a door
          that scoped the app into that one career; an account is one career
          now, so the only card left would be a door to the screen you are
          already standing on. "Add another performer" is deleted with it —
          creating a second aa_subject in this SAME tenant is exactly the
          shape the ruling ends. A guardian setting up a MINOR's own,
          separate account is real (see the Hold list on the brief) but is
          not built here: it needs a new account and a grant of authority on
          it, not a second performer in this one. Flagged as a gap, not
          papered over — see this chunk's report. */}
    </div>
  );
}

/** How far ahead the band looks. A call sheet three weeks out is not news,
 * and showing it every morning for three weeks teaches the parent to ignore
 * that part of the screen — which costs more than it ever gains. Two days
 * covers "today" and "tomorrow", which is the whole question. */
const HORIZON_DAYS = 2;

/** "Today" / "Tomorrow" / a weekday. The parent does not want to do date
 * arithmetic before coffee. */
function bandWhen(v: unknown): string {
  if (typeof v !== 'string' || !v.trim()) return 'Coming up';
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(v.trim());
  const d = ymd ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])) : new Date(v);
  if (Number.isNaN(d.getTime())) return 'Coming up';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((d.getTime() - today.getTime()) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return d.toLocaleDateString(undefined, { weekday: 'long' });
}

/** The pickup time, or null.
 *
 * ⚑ NULL RENDERS THE MISSING FACT, NOT A LABEL (Huan's own reading of the
 * band, 2026-09-02). This slot said "call sheet" at 1.75rem, which is the
 * record's KIND set in the size reserved for its most important number — a
 * headline that tells a parent nothing. What they need at a glance is that
 * nobody has said when to be there yet, because that is the thing to chase.
 * Inventing a 00:00 is still worse than either. */
function bandPickup(v: unknown): string | null {
  if (typeof v !== 'string' || !v.trim()) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

// ⚑ REMOVED, NOT WEAKENED (B7, 2026-09-24, `defa9aed`, item 2). This used to
// hold WEEK_DAYS/WEEK_CAP, the WeekItem shape, localDay/weekDayWord and
// weekAhead() — the older "this week" strip (holds + sessions, above "Needs
// you"). "Coming up" (B4, below) supersedes it: same week, every dated thing
// the shared calendar assembler returns, not just two kinds of date. Nothing
// else in this file or its tests read these exports — see this chunk's
// report for the grep that confirmed it.
